import { jsx, jsxs } from "react/jsx-runtime";
import React__default, { useRef, useState, useEffect } from "react";
import classNames from "classnames";
import { theme } from "../../theme/index.js";
import "../../hooks/useBreakpoint.js";
import { useHover } from "../../hooks/useHover.js";
import { useToggle } from "../../hooks/useToggle.js";
import { useKeyboardEvent } from "../../hooks/useKeyboardEvent.js";
import { useOutsideEvent } from "../../hooks/useOutsideEvent.js";
import { useUuid } from "../../hooks/useUuid.js";
import { LanguageLocales, ZIndex, KeyboardEventKey, AnalyticsId, IconSize } from "../../constants.js";
import { isComponent } from "../../utils/component.js";
import { useLanguage } from "../../utils/language.js";
import { mergeRefs } from "../../utils/refs.js";
import { B as Button } from "../../Button.js";
import { C as Checkbox } from "../../Checkbox.js";
import { I as Icon } from "../../Icon.js";
import PlusSmall from "../Icons/PlusSmall.js";
import { R as RadioButton } from "../../RadioButton.js";
import styles from "./styles.module.scss";
const closeText$1 = "Close";
const enGB = {
  closeText: closeText$1
};
const closeText = "Lukk";
const nbNO = {
  closeText
};
const getResources = (language) => {
  switch (language) {
    case LanguageLocales.ENGLISH:
      return enGB;
    case LanguageLocales.NORWEGIAN:
    default:
      return nbNO;
  }
};
var DropdownOnColor = /* @__PURE__ */ ((DropdownOnColor2) => {
  DropdownOnColor2["onwhite"] = "onwhite";
  DropdownOnColor2["ongrey"] = "ongrey";
  DropdownOnColor2["onblueberry"] = "onblueberry";
  DropdownOnColor2["oncherry"] = "oncherry";
  return DropdownOnColor2;
})(DropdownOnColor || {});
const Dropdown = (props) => {
  const {
    label,
    placeholder,
    noCloseButton = false,
    onToggle,
    dropdownMinWidth,
    open = false,
    children,
    onColor = "onwhite",
    transparent = false,
    fluid = false,
    testId,
    disabled,
    zIndex = ZIndex.PopOver,
    resources
  } = props;
  const dropdownRef = useRef(null);
  const optionsRef = useRef(null);
  const { hoverRef: buttonRef, isHovered } = useHover();
  const openedByKeyboard = useRef(false);
  const { value: isOpen, toggleValue: toggleIsOpen } = useToggle(!disabled && open, onToggle);
  const inputRefList = useRef(React__default.Children.map(children, () => React__default.createRef()));
  const [currentIndex, setCurrentIndex] = useState();
  const labelId = useUuid();
  const toggleLabelId = useUuid();
  const optionIdPrefix = useUuid();
  const { language } = useLanguage(LanguageLocales.NORWEGIAN);
  const defaultResources = getResources(language);
  const mergedResources = {
    ...defaultResources,
    ...resources,
    closeText: props.closeText ?? (resources == null ? void 0 : resources.closeText) ?? defaultResources.closeText
  };
  const handleOpen = (isKeyboard) => {
    openedByKeyboard.current = isKeyboard;
    toggleIsOpen();
  };
  const handleClose = () => {
    var _a;
    toggleIsOpen();
    (_a = buttonRef.current) == null ? void 0 : _a.focus();
  };
  useEffect(() => {
    var _a, _b, _c;
    if (isOpen && openedByKeyboard.current) {
      (_c = (_b = (_a = inputRefList.current) == null ? void 0 : _a.find((inputRef, index) => {
        if (inputRef.current && !inputRef.current.hasAttribute("disabled")) {
          setCurrentIndex(index);
          return true;
        }
      })) == null ? void 0 : _b.current) == null ? void 0 : _c.focus();
    }
  }, [isOpen]);
  const handleKeyboardNavigation = (event) => {
    var _a;
    if (!inputRefList.current) {
      return;
    }
    if (event.key === KeyboardEventKey.Escape) {
      if (isOpen) handleClose();
      return;
    }
    if (!isOpen) {
      handleOpen(true);
      event.preventDefault();
      return;
    }
    const index = inputRefList.current.findIndex((x) => x.current === event.target);
    let nextIndex = index;
    if (event.key === KeyboardEventKey.Home) {
      nextIndex = 0;
    } else if (event.key === KeyboardEventKey.End) {
      nextIndex = inputRefList.current.length - 1;
    } else if (event.key === KeyboardEventKey.ArrowDown && index < inputRefList.current.length - 1) {
      nextIndex = index + 1;
    } else if (event.key === KeyboardEventKey.ArrowUp && index > 0) {
      nextIndex = index - 1;
    } else if (event.key === KeyboardEventKey.Enter && index !== -1) {
      nextIndex = index;
    }
    if (nextIndex !== -1 && event.key !== KeyboardEventKey.Space) {
      event.preventDefault();
      (_a = inputRefList.current[nextIndex].current) == null ? void 0 : _a.focus();
      setCurrentIndex(nextIndex);
    }
  };
  useKeyboardEvent(dropdownRef, handleKeyboardNavigation, [
    KeyboardEventKey.ArrowDown,
    KeyboardEventKey.ArrowUp,
    KeyboardEventKey.End,
    KeyboardEventKey.Enter,
    KeyboardEventKey.Escape,
    KeyboardEventKey.Home,
    KeyboardEventKey.Space
  ]);
  useOutsideEvent(dropdownRef, () => isOpen && handleClose());
  const toggleClasses = classNames(
    styles.dropdown__toggle,
    !disabled && {
      [styles["dropdown__toggle--on-white"]]: onColor === "onwhite",
      [styles["dropdown__toggle--on-grey"]]: onColor === "ongrey",
      [styles["dropdown__toggle--on-blueberry"]]: onColor === "onblueberry",
      [styles["dropdown__toggle--on-cherry"]]: onColor === "oncherry",
      [styles["dropdown__toggle--transparent"]]: transparent,
      [styles["dropdown__toggle--fluid"]]: fluid,
      [styles["dropdown__toggle--open"]]: isOpen
    }
  );
  const contentClasses = classNames(styles.dropdown__content, isOpen && styles["dropdown__content--open"]);
  const renderChildren = React__default.Children.map(children, (child, index) => {
    const role = isComponent(child, RadioButton) ? "menuitemradio" : isComponent(child, Checkbox) ? "menuitemcheckbox" : "menuitem";
    return /* @__PURE__ */ jsx("li", { className: styles.dropdown__input, role, id: `${optionIdPrefix}-${index}`, children: React__default.isValidElement(child) && inputRefList.current && inputRefList.current[index] ? React__default.cloneElement(child, { ref: mergeRefs([child.props.ref, inputRefList.current[index]]) }) : child });
  });
  return /* @__PURE__ */ jsxs("div", { className: styles.dropdown, ref: dropdownRef, children: [
    /* @__PURE__ */ jsx("span", { id: labelId, className: styles.dropdown__label, children: label }),
    /* @__PURE__ */ jsxs(
      "button",
      {
        type: "button",
        onClick: () => handleOpen(false),
        className: toggleClasses,
        ref: buttonRef,
        "data-testid": testId,
        "data-analyticsid": AnalyticsId.Dropdown,
        disabled,
        "aria-labelledby": toggleLabelId,
        "aria-haspopup": "menu",
        "aria-expanded": isOpen,
        children: [
          /* @__PURE__ */ jsx("span", { id: toggleLabelId, className: styles.dropdown__toggle__label, children: placeholder }),
          /* @__PURE__ */ jsx(
            Icon,
            {
              color: disabled ? theme.palette.neutral700 : theme.palette.blueberry600,
              svgIcon: PlusSmall,
              className: styles.dropdown__icon,
              isHovered: !disabled && isHovered,
              size: IconSize.XSmall
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsxs("div", { className: contentClasses, style: { width: fluid ? "100%" : `auto`, minWidth: dropdownMinWidth ?? "auto", zIndex }, children: [
      /* @__PURE__ */ jsx(
        "ul",
        {
          className: styles.dropdown__options,
          role: "menu",
          "aria-labelledby": labelId,
          tabIndex: -1,
          "aria-activedescendant": typeof currentIndex !== "undefined" ? `${optionIdPrefix}-${currentIndex}` : void 0,
          ref: optionsRef,
          children: renderChildren
        }
      ),
      !noCloseButton && /* @__PURE__ */ jsx("div", { className: styles.dropdown__close, children: /* @__PURE__ */ jsx(Button, { onClick: handleClose, "aria-expanded": isOpen, children: mergedResources.closeText }) })
    ] })
  ] });
};
export {
  DropdownOnColor,
  Dropdown as default
};
//# sourceMappingURL=index.js.map
